import { CheckCircle, Copy, XCircle } from '@phosphor-icons/react';
import { differenceInSeconds } from 'date-fns';
import { useRef, useState, useEffect, useMemo } from 'react';
import { CodeBlock } from 'react-code-blocks';
import { useNavigate } from 'react-router-dom';
import { useNotification } from 'src/contexts/NotificationContext';
import { usePlayRecording } from 'src/contexts/PlayRecordingContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useCampaigns } from 'src/hooks/useCampaigns';
import { Agent } from 'src/interfaces/agent.interface';
import { Call, PostCallResults } from 'src/interfaces/call.interface';
import type { Campaign } from 'src/services/campaigns/campaigns.interface';
import { cn } from 'src/utils/className';
import { copyToClipboard } from 'src/utils/copyToClipboard';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';
import { formatSnakeCase } from 'src/utils/string';
import { Button } from './Button';
import {
  CallRecordingButton,
  CallRecordingButtonRef,
} from './CallRecordingButton';
import { CopyableField } from './CopyableField';
import { Modal } from './Modal';
import { CallErrorDisplay } from './CallErrorDisplay';
import { BetaTag } from './BetaTag';
import { Tooltip } from './Tooltip';
import { isCampaignWorkflow } from 'src/services/campaigns/campaigns.helpers';

type CallDetailsModalProps = {
  call: Call;
  isOpen: boolean;
  onClose: () => void;
};

type TranscriptMessage = {
  timestamp: string;
  speaker: 'HUMAN' | 'BOT';
  message: string;
};

const FLUENTS_CONTEXT_VARIABLE_PREFIX = '$fluents';

const CAMPAIGN_FIELD_KEY = '$ui-only:campaign';

const WORKFLOW_REFERENCE_CONTEXT_KEY =
  '$fluents:workflows:workflow_reference_id';

const ORDERED_FIELDS = [
  'id',
  'status',
  'start_time',
  'end_time',
  'from_number',
  'to_number',
  'agent',
  CAMPAIGN_FIELD_KEY,
  'human_detection_result',
  'recording_available',
  'telephony_provider',
  'post_call_results',
  'context',
  'avg_latency',
  'max_latency',
];

const EXCLUDED_FIELDS = [
  'transcript',
  'telephony_metadata',
  'user_id',
  'action_history',
  'recording_available',
];

const ROW_WIDTH_FIELDS = ['context', 'post_call_results'];

const fieldLabels: Record<string, string> = {
  id: 'ID',
  status: 'Raw Status',
  human_detection_result: 'Human Answered',
  recording_available: 'Recording',
  from_number: 'From Number',
  to_number: 'To Number',
  agent: 'Agent',
  [CAMPAIGN_FIELD_KEY]: 'Campaign',
  telephony_provider: 'Provider',
  start_time: 'Start Time',
  end_time: 'End Time',
  context: 'Context',
};

const stripFluentsContextFields = (
  context: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(context).filter(
      ([key]) => !key.startsWith(FLUENTS_CONTEXT_VARIABLE_PREFIX),
    ),
  );

const parseTranscript = (transcript: string) => {
  if (!transcript) return [];

  const lines = transcript.split('\n').filter((line) => line.trim());
  const messages: Array<TranscriptMessage> = [];

  lines.forEach((line) => {
    const match = line.match(/^\[([^\]]+)\]\s*(HUMAN|BOT):\s*(.+)$/);
    if (match) {
      messages.push({
        timestamp: match[1],
        speaker: match[2] as 'HUMAN' | 'BOT',
        message: match[3].trim(),
      });
    }
  });

  return messages;
};

export const CallDetailsModal = ({
  call,
  isOpen,
  onClose,
}: CallDetailsModalProps) => {
  const { get } = useAuthedRequest();
  const campaignsService = useCampaigns();
  const navigate = useNavigate();
  const notification = useNotification();
  const { triggerPlay, audioUrl, audioRef, cleanupAudio } = usePlayRecording();
  const callRecordingButtonRef = useRef<CallRecordingButtonRef>(null);

  const [currentPlayerTime, setCurrentPlayerTime] = useState(0);
  const [agent, setAgent] = useState<Agent | undefined>(undefined);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const workflowReferenceId = call.context?.[WORKFLOW_REFERENCE_CONTEXT_KEY] as
    | string
    | undefined;

  useEffect(() => {
    const fetchAgent = async () => {
      const agentId =
        typeof call.agent === 'string' ? call.agent : call.agent?.id;
      const agent = await get(`/agents/${agentId}`);
      setAgent(agent);
    };
    fetchAgent();
  }, [call.agent, get]);

  useEffect(() => {
    if (!workflowReferenceId || !isCampaignWorkflow(workflowReferenceId)) {
      setCampaign(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const fetchedCampaign =
          await campaignsService.fetchCampaign(workflowReferenceId);
        if (!cancelled) {
          setCampaign(fetchedCampaign);
        }
      } catch (error) {
        console.error(
          `Failed to fetch campaign referenceId=${workflowReferenceId}`,
          error,
        );
        if (!cancelled) {
          setCampaign(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campaignsService, workflowReferenceId]);

  const startDate = call.start_time
    ? new Date(Number(call.start_time) * 1000)
    : null;
  const endDate = call.end_time ? new Date(Number(call.end_time) * 1000) : null;
  const durationSecs =
    (startDate && endDate && differenceInSeconds(endDate, startDate)) || 0;

  const downloadTranscript = () => {
    if (!call.transcript) return;

    const element = document.createElement('a');
    const file = new Blob([call.transcript], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'transcript_' + call.id + '.txt';
    document.body.appendChild(element);
    element.click();
  };

  const formatFieldValue = (key: string, value: unknown): React.ReactNode => {
    // First render UI only fields.
    if (key === CAMPAIGN_FIELD_KEY) {
      if (!workflowReferenceId) {
        return '-';
      }

      const campaignName = campaign?.name;

      return (
        <div className="flex flex-col gap-1">
          {campaignName && (
            <span className="text-sm font-medium text-neutral-800">
              {campaignName}
            </span>
          )}
          <CopyableField
            value={campaign?.referenceId || workflowReferenceId}
            notification={notification}
          />
        </div>
      );
    }

    // Now render fields that map to actual values in the call object.
    let displayValue = value;
    if (key === 'context') {
      if (typeof displayValue === 'object' && displayValue !== null) {
        displayValue = stripFluentsContextFields(
          displayValue as Record<string, unknown>,
        );
      }
    } else if (key === 'post_call_results') {
      displayValue = (displayValue as PostCallResults)?.content;
      if (typeof displayValue === 'string' && displayValue.trim() !== '') {
        const stringValue = displayValue as string;

        try {
          displayValue = JSON.parse(stringValue);
        } catch {
          return <div className="whitespace-pre-wrap">{stringValue}</div>;
        }
      }
    }

    if (
      displayValue === null ||
      displayValue === undefined ||
      displayValue === ''
    )
      return '-';
    if (Array.isArray(displayValue) && displayValue.length === 0) return '-';
    if (
      typeof displayValue === 'object' &&
      !Array.isArray(displayValue) &&
      Object.keys((displayValue as Record<string, unknown>) || {}).length === 0
    ) {
      return '-';
    }

    if (key === 'start_time') {
      return friendlyDateFormatter(startDate);
    }

    if (key === 'end_time') {
      return friendlyDateFormatter(endDate);
    }

    if (key === 'from_number' || key === 'to_number') {
      return processPhoneNumber(displayValue?.toString() || '');
    }

    if (key === 'human_detection_result') {
      if (durationSecs === 0) {
        return '-';
      } else if (displayValue === 'no_human') {
        return (
          <span className="flex items-center gap-2">
            <XCircle
              size={20}
              weight="fill"
              className="flex-shrink-0 text-error"
            />
            <span style={{ transform: 'translateY(2px)' }}>No</span>
          </span>
        );
      } else {
        return (
          <span className="flex items-center gap-2 ">
            <CheckCircle
              size={20}
              weight="fill"
              className="flex-shrink-0 text-success"
            />
            <span style={{ transform: 'translateY(2px)' }}>Yes</span>
          </span>
        );
      }
    }

    if (key === 'recording_available') {
      return displayValue ? 'Available' : 'Not Available';
    }

    if (key === 'id') {
      return (
        <CopyableField
          value={displayValue?.toString() || '-'}
          notification={notification}
        />
      );
    }

    if (key === 'agent') {
      if (typeof displayValue === 'string') {
        return (
          <CopyableField value={displayValue} notification={notification} />
        );
      }

      const agentValue = displayValue as { id?: string; name?: string };
      return (
        <CopyableField value={agentValue.id} notification={notification} />
      );
    }

    if (key === 'telephony_id') {
      return (
        <CopyableField
          value={call.telephony_metadata?.call_sid || call.telephony_id || '-'}
          notification={notification}
        />
      );
    }

    if (key === 'errors') {
      return (
        <div className="max-h-40 overflow-y-auto">
          <CallErrorDisplay
            errors={displayValue as string[]}
            showWarning={false}
          />
        </div>
      );
    }

    if (typeof displayValue === 'object') {
      return (
        <div className="relative">
          <button
            type="button"
            className="absolute top-2 right-2 z-10 border-none m-0 p-0"
            onClick={() => {
              copyToClipboard(
                JSON.stringify(displayValue, null, 2),
                notification,
              );
            }}
            aria-label="Copy JSON"
          >
            <Copy
              className="cursor-pointer w-5 h-5 text-neutral-700"
              size={16}
            />
          </button>

          <CodeBlock
            text={JSON.stringify(displayValue, null, 2)}
            language="json"
            wrapLongLines={false}
            showLineNumbers={false}
          />
        </div>
      );
    }

    return displayValue?.toString() || '-';
  };

  const isRowWidthField = (field: string | undefined) =>
    !!field && ROW_WIDTH_FIELDS.includes(field);

  const DetailsFullRow = ({ fieldKey }: { fieldKey: string }) => (
    <tr>
      <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
        {fieldLabels[fieldKey] || formatSnakeCase(fieldKey)}
      </td>
      <td className="py-2 px-4 border-b border-neutral-100 w-[80%]" colSpan={3}>
        {formatFieldValue(
          fieldKey,
          (call as Record<string, unknown>)[fieldKey],
        )}
      </td>
    </tr>
  );

  const DetailsStandardRow = ({
    firstKey,
    secondKey,
  }: {
    firstKey: string;
    secondKey?: string;
  }) => (
    <tr>
      <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
        {fieldLabels[firstKey] || formatSnakeCase(firstKey)}
      </td>
      <td className="py-2 px-4 border-b border-neutral-100 w-[30%] break-all">
        {formatFieldValue(
          firstKey,
          (call as Record<string, unknown>)[firstKey],
        )}
      </td>

      {secondKey ? (
        <>
          <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
            {fieldLabels[secondKey] || formatSnakeCase(secondKey)}
          </td>
          <td className="py-2 px-4 border-b border-neutral-100 w-[30%]">
            {formatFieldValue(
              secondKey,
              (call as Record<string, unknown>)[secondKey],
            )}
          </td>
        </>
      ) : (
        <>
          <td className="py-2 px-4 border-b border-neutral-100 w-[20%]"></td>
          <td className="py-2 px-4 border-b border-neutral-100 w-[30%]"></td>
        </>
      )}
    </tr>
  );

  const getTelephonyIdRow = () => {
    if (!call.telephony_provider) return null;

    const label = `${formatSnakeCase(call.telephony_provider)} ID`;
    return (
      <tr key="telephony_id">
        <td className="py-2 px-4 border-b border-neutral-100 font-medium w-[20%]">
          {label}
        </td>
        <td
          className="py-2 px-4 border-b border-neutral-100 break-all text-ellipsis whitespace-nowrap overflow-hidden"
          colSpan={3}
        >
          <CopyableField
            value={
              call.telephony_metadata?.call_sid || call.telephony_id || '-'
            }
            notification={notification}
          />
        </td>
      </tr>
    );
  };

  const remainingFields = call
    ? Object.keys(call).filter(
        (key) =>
          !ORDERED_FIELDS.includes(key) &&
          !EXCLUDED_FIELDS.includes(key) &&
          call[key as keyof Call] !== null &&
          call[key as keyof Call] !== undefined &&
          call[key as keyof Call] !== '',
      )
    : [];

  const displayFields = [...ORDERED_FIELDS, ...remainingFields];
  const transcriptMessages = parseTranscript(call.transcript || '');

  const parseTimestampToSeconds = (timestamp: string): number => {
    const parts = timestamp.split(':').map((p) => p.trim());
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (parts.length === 3) {
      hours = Number(parts[0]);
      minutes = Number(parts[1]);
      seconds = Number(parts[2]);
    } else if (parts.length === 2) {
      minutes = Number(parts[0]);
      seconds = Number(parts[1]);
    } else if (parts.length === 1) {
      seconds = Number(parts[0]);
    }

    hours = isNaN(hours) ? 0 : hours;
    minutes = isNaN(minutes) ? 0 : minutes;
    seconds = isNaN(seconds) ? 0 : seconds;

    return hours * 3600 + minutes * 60 + seconds;
  };

  const handleMessageClick = async (messageIndex: number) => {
    let targetTime = 0;

    if (messageIndex === 0) {
      targetTime = 0;
    } else {
      const prevMessage = transcriptMessages[messageIndex - 1];

      if (prevMessage) {
        targetTime = parseTimestampToSeconds(prevMessage.timestamp);
      }
    }

    triggerPlay(targetTime);

    if (!audioUrl) {
      callRecordingButtonRef.current?.click();
    }
  };

  const handleClose = () => {
    onClose();
    audioRef.current?.pause();
    cleanupAudio();
  };

  const timestampToSeconds = (timestamp: string) => {
    const parts = timestamp
      .split(':')
      .map((p) => p.trim())
      .reverse();
    const seconds = Number(parts[0]) || 0;
    const minutes = Number(parts[1]) || 0;
    const hours = Number(parts[2]) || 0;
    return hours * 3600 + minutes * 60 + seconds;
  };

  const currentTranscriptMessageIndex = useMemo(() => {
    if (transcriptMessages.length === 0) return -1;

    const messagesWithRanges = transcriptMessages.map((msg, index) => {
      const endTime = timestampToSeconds(msg.timestamp);
      const startTime =
        index === 0
          ? 0
          : timestampToSeconds(transcriptMessages[index - 1].timestamp);

      return {
        ...msg,
        startTime,
        endTime,
      };
    });
    const index = messagesWithRanges.findIndex(
      ({ startTime, endTime }) =>
        currentPlayerTime >= startTime && currentPlayerTime < endTime,
    );

    return index !== -1 ? index : transcriptMessages.length - 1;
  }, [currentPlayerTime, transcriptMessages]);

  useEffect(() => {
    if (currentTranscriptMessageIndex < 0) {
      return;
    }

    const messageElement = document.getElementById(
      `message-${currentTranscriptMessageIndex}`,
    );
    if (messageElement) {
      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentTranscriptMessageIndex]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Call Overview"
      className="lg:max-w-[90%] max-w-7xl"
    >
      <div className="flex gap-6 h-[78vh]">
        <div className="flex-1 flex flex-col">
          {call.transcript && (
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Transcript</h3>
              <div className="flex gap-2">
                <Tooltip
                  content="Only available for OpenAI agents with human interaction"
                  zIndex={50}
                  position="bottom"
                >
                  <Button
                    onClick={() =>
                      navigate(`/calls/${call.id}/conversation-editor`)
                    }
                    size="small"
                    color="secondary"
                    className="gap-2"
                    disabled={
                      !call.transcript ||
                      parseTranscript(call.transcript).length <= 1 ||
                      (agent?.provider && agent.provider !== 'openai')
                    }
                  >
                    Edit Conversation
                    <BetaTag size="small" />
                  </Button>
                </Tooltip>
                <Button onClick={downloadTranscript} size="small">
                  Download
                </Button>
              </div>
            </div>
          )}

          {call.transcript && (
            <div className="flex-1 overflow-y-auto border border-neutral-200 rounded-lg p-4 bg-neutral-50">
              {transcriptMessages.length > 0 && (
                <div className="space-y-4">
                  {transcriptMessages.map((msg, index) => {
                    const isHuman = msg.speaker === 'HUMAN';
                    const isCurrentMessage =
                      index === currentTranscriptMessageIndex;

                    return (
                      <div
                        key={index}
                        id={`message-${index}`}
                        className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          onClick={() => handleMessageClick(index)}
                          className={cn(
                            `max-w-[80%] rounded-lg px-4 py-2 cursor-pointer transition-opacity ${
                              isHuman
                                ? 'bg-primary text-neutral-0 hover:opacity-80'
                                : 'bg-neutral-0 text-neutral-800 border border-neutral-200 hover:opacity-60'
                            }`,
                            isCurrentMessage && currentPlayerTime > 0
                              ? `border-2 shadow-lg border-gold-300`
                              : '',
                          )}
                        >
                          <div className="text-xs opacity-70 mb-1">
                            {msg.timestamp} - {msg.speaker}
                          </div>
                          <div className="whitespace-pre-wrap">
                            {msg.message}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {transcriptMessages.length === 0 && (
                <div className="text-neutral-500 italic">{call.transcript}</div>
              )}
            </div>
          )}

          {!call.transcript && (
            <div className="flex-1 border border-neutral-200 rounded-lg p-4 bg-neutral-50 flex items-center justify-center">
              <p className="text-neutral-400">No transcript available.</p>
            </div>
          )}

          {call.recording_available ? (
            <div className="flex justify-center mt-4">
              <CallRecordingButton
                ref={callRecordingButtonRef}
                callId={call.id}
                recordingAvailable={call.recording_available}
                setCurrentTime={setCurrentPlayerTime}
                showTimeline
              />
            </div>
          ) : (
            <div className="flex justify-center mt-4">
              <p className="text-neutral-400">No recording available.</p>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col">
          <h3 className="text-lg font-semibold mb-4">Details</h3>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full border border-neutral-100 bg-neutral-0 table-fixed">
              <tbody>
                {(() => {
                  const validFields = displayFields.filter((key) => {
                    if (EXCLUDED_FIELDS.includes(key)) return false;
                    if (key === CAMPAIGN_FIELD_KEY) {
                      return Boolean(
                        workflowReferenceId &&
                          isCampaignWorkflow(workflowReferenceId),
                      );
                    }

                    const value = call[key as keyof Call];
                    if (value === null || value === undefined || value === '')
                      return false;
                    if (Array.isArray(value) && value.length === 0)
                      return false;
                    if (
                      typeof value === 'object' &&
                      !Array.isArray(value) &&
                      value !== null &&
                      Object.keys(value as Record<string, unknown>).length === 0
                    )
                      return false;

                    return true;
                  });

                  const rows: JSX.Element[] = [];

                  let i = 0;
                  while (i < validFields.length) {
                    const firstKey = validFields[i];
                    const secondKey = validFields[i + 1];

                    // If the first key is a full-width field, let it span the full row
                    if (isRowWidthField(firstKey)) {
                      rows.push(
                        <DetailsFullRow key={firstKey} fieldKey={firstKey} />,
                      );
                      i += 1;
                      continue;
                    }

                    // If the second key is a full-width field, render the first as a normal half-row
                    // and then render the full-width field as its own row
                    if (isRowWidthField(secondKey)) {
                      rows.push(
                        <DetailsStandardRow
                          key={firstKey}
                          firstKey={firstKey}
                        />,
                      );

                      rows.push(
                        <DetailsFullRow
                          key={secondKey}
                          fieldKey={secondKey as string}
                        />,
                      );

                      i += 2;
                      continue;
                    }

                    rows.push(
                      <DetailsStandardRow
                        key={firstKey}
                        firstKey={firstKey}
                        secondKey={secondKey}
                      />,
                    );

                    i += 2;
                  }

                  return rows;
                })()}

                {getTelephonyIdRow()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
};
